import PropTypes from 'prop-types';
import { Button, Card, Heading, Inline, RadioGroup, Stack, Text } from '@etg/wings';
import { CarrierLogo, ContentWrapper, ExpandableContent, Overlay } from '@eti/components';
import { css, mediaQueries } from '@eti/styles';
import {
  BoundNew,
  BrandedFare as BrandedFareType,
  CarrierNew,
  ExtraProduct,
  TravelerPrice,
} from '@eti/types';
import { CaretDown } from '@phosphor-icons/react';
import { useProperty, useTranslation } from '@eti/providers';
import { useEffect, useState, useMemo } from 'react';
import { MULTI_STOP, ONE_WAY, RETURN } from '../../../../constants/tripTypesConstants';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import TripDetailsTitle from '../TripDetailsTitle';
import ItineraryBound from '../../../order-details/components/overview/itinerary/drawer/ItineraryBound';
import { RECOMMENED_FAREKEY } from '../../../../constants/brandedFareConstants';
import StickyFooter from '../sticky-footer/StickyFooter';
import { getTotalPrice } from '../v3/utils';
import usePriceDisplayType from '../v3/branded-fares/hooks/usePriceDisplayType';
import usePreSelectedExtraProducts from '../v3/branded-fares/hooks/usePreSelectedExtraProducts';
import { GFSPartner } from '../v3/branded-fares/constants';
import useUpdateProductPriceBySelectedFare from '../v3/branded-fares/hooks/useUpdateProductPriceBySelectedFare';
import BrandedFare from './BrandedFare';

const listingWrapperStyles = (isMediumScreenWidth, numberOfItems) => css`
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 1fr;
  width: 100%;

  @media ${mediaQueries.medium.up} {
    grid-template-columns: ${numberOfItems < 3 || isMediumScreenWidth ? '1fr 1fr' : '1fr 1fr 1fr'};
  }
`;

const iconSizeStyles = css`
  height: 32px;
  width: 32px;
`;

const optionsLengthStyles = css`
  color: #737373;
`;

const toggleButtonWrapperStyles = css`
  display: flex;
  margin-top: 8px;
  position: relative;
`;

const toggleButtonStyles = css`
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
`;

const iconStyles = (isShowingAllOptions) => css`
  transform: rotate(${isShowingAllOptions ? '180deg' : '0deg'});
`;

const cardStyles = (isContentToggleable) => css`
  margin-top: 16px;
  ${isContentToggleable && `padding-bottom: 48px`};
`;

const headingWrapperStyles = css`
  justify-content: space-between;

  @media ${mediaQueries.small.only} {
    flex-direction: column;
    gap: 8px;
  }
`;

const contentWrapperStyles = css`
  margin-bottom: 24px;
`;

const cardsContainer = css`
  margin-top: 16px;
`;

const logoContainerStyles = css`
  gap: 8px;

  @media ${mediaQueries.small.only} {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 8px;
  }
`;

const modifyTripBrandedFareName = (bounds, brandedFares, defaultSelectedFareKey) => {
  const { name } =
    brandedFares?.find(({ selectionKey }) => selectionKey === defaultSelectedFareKey) || {};

  return bounds.map((bound) => {
    return {
      ...bound,
      segments: bound.segments.map((segment) => {
        switch (segment?.__typename) {
          case 'TripSegment':
            return { ...segment, brandedFareInformation: { name, __typename: 'BrandedFareInfo' } };
          case 'EventSegment':
            return segment;
          default:
            return null;
        }
      }),
    };
  });
};

const useBrandedFareOptions = (
  brandedFares,
  selectedBrandedFareSelectionKey,
  isMediumScreenWidth,
) => {
  useEffect(() => {
    sessionStorage.setItem('brandedFareOffered', 'true');
  }, []);
  const { p } = useProperty();
  const numberOfBrandedFaresToShow = isMediumScreenWidth ? 2 : 3;
  const isContentToggleable = brandedFares.length > numberOfBrandedFaresToShow;
  const alwaysVisibleOptions = brandedFares.slice(0, numberOfBrandedFaresToShow);
  const toggleableOptions = isContentToggleable
    ? brandedFares.slice(numberOfBrandedFaresToShow)
    : null;

  const isSelectedBrandedFareAmongToggleableOptions = toggleableOptions?.some(
    (toggleableOption) => toggleableOption.selectionKey === selectedBrandedFareSelectionKey,
  );

  const { isOpen, handleToggle } = useToggle(
    isContentToggleable &&
      (p('BrandedFares.ShowMore.DefaultOpen') || isSelectedBrandedFareAmongToggleableOptions),
  );

  return { alwaysVisibleOptions, handleToggle, isContentToggleable, isOpen, toggleableOptions };
};

const shouldShowInformationBox = (carriersToHideInformationBox, ticketingCarrierCodes) => {
  const hideInformationBoxForCarrier = ticketingCarrierCodes?.some((ticketingCarrierCode) =>
    carriersToHideInformationBox?.includes(ticketingCarrierCode),
  );
  return !hideInformationBoxForCarrier;
};

export const getDefaultAndRecommendedFare = (travelerPrices, brandedFares) => {
  const defaultFarePrice = travelerPrices[0]?.price.price.value;
  const defaultSelectedBrandedFareIndex = brandedFares.findIndex(
    (brandedFare) => brandedFare.travelerPrices[0]?.price.price.value === defaultFarePrice,
  );
  if (!localStorage.getItem(RECOMMENED_FAREKEY)) {
    localStorage.setItem(
      RECOMMENED_FAREKEY,
      brandedFares[defaultSelectedBrandedFareIndex + 1]?.selectionKey,
    );
  }
  return {
    defaultSelectedFareKey: brandedFares[defaultSelectedBrandedFareIndex]?.selectionKey,
    recommendedFareKey: localStorage.getItem(RECOMMENED_FAREKEY),
  };
};

// TODO WEB-5485 write flowtests when this component uses real (mocked) data.
const BrandedFares = ({
  availableExtraProducts,
  brandedFares = [],
  defaultSelectedFareKey,
  discountAmount,
  handleProceedButtonClick,
  paymentMethodPrices,
  selectedBrandedFareSelectionKey,
  setSelectedBrandedFareSelectionKey,
  ticketingCarrierCodes,
  isMediumScreenWidth,
  isSelectTripLoading,
  marketingCarrier,
  partner,
  recommendedFareKey,
  bounds,
  tripType,
  tripTravelers,
  travelerPrices,
}) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { priceDisplayType } = usePriceDisplayType();

  const regexp = new RegExp(`^${GFSPartner}`);
  const isPartnerGoogleFlights = regexp.test(partner);
  const preSelectedExtraProducts = usePreSelectedExtraProducts(
    availableExtraProducts,
    travelerPrices?.length,
    isPartnerGoogleFlights,
  );
  const finalProductIdsToPurchase = useMemo(() => {
    const productIdsToPurchase = preSelectedExtraProducts.map(({ productId }) => productId);
    return [...new Set(productIdsToPurchase)];
  }, [preSelectedExtraProducts]);

  const [showTripDetails, setShowTripDetails] = useState(false);
  const { alwaysVisibleOptions, handleToggle, isContentToggleable, isOpen, toggleableOptions } =
    useBrandedFareOptions(brandedFares, defaultSelectedFareKey, isMediumScreenWidth);

  const shouldShowInformationBoxInFirstOption = shouldShowInformationBox(
    p('BrandedFares.HideInformationBox.Carrier'),
    ticketingCarrierCodes,
  );

  const modifiedBounds = defaultSelectedFareKey
    ? modifyTripBrandedFareName(bounds, brandedFares, defaultSelectedFareKey)
    : bounds;

  const handleSelectBrandedFare = (event) => {
    setSelectedBrandedFareSelectionKey(event.target.value);
  };

  const toggleTripDetailsDrawer = () => {
    setShowTripDetails(!showTripDetails);
  };

  const brandedFareDescription = t('BrandedFares.Text');
  const selectedFareKey = selectedBrandedFareSelectionKey || defaultSelectedFareKey;
  const selectedBrandedFare = brandedFares.find(
    ({ selectionKey }) => selectionKey === selectedFareKey,
  );

  const priceUpdatedProducts = useUpdateProductPriceBySelectedFare(
    preSelectedExtraProducts,
    selectedFareKey,
    brandedFares,
    travelerPrices?.length,
  );

  const totalPrice = getTotalPrice(
    selectedBrandedFare,
    priceDisplayType,
    finalProductIdsToPurchase,
    priceUpdatedProducts,
  );
  return (
    <>
      <ContentWrapper className={contentWrapperStyles}>
        <Card className={cardStyles(isContentToggleable)} data-testid="brandedFares">
          <RadioGroup
            name="brandedFares"
            onChange={handleSelectBrandedFare}
            selectedValue={selectedBrandedFareSelectionKey || defaultSelectedFareKey}
          >
            <Stack spacing={8}>
              <Inline className={headingWrapperStyles}>
                <Heading as="legend" data-testid="brandedFares-v2-header" level={1}>
                  {t('BrandedFares.Header')}
                </Heading>
                <Stack
                  align="end"
                  className={logoContainerStyles}
                  data-testid="count-of-branded-fares-v2-visible"
                  spacing={0}
                >
                  <CarrierLogo
                    className={iconSizeStyles}
                    data-testid="tripDetails-carrier-logo"
                    id={marketingCarrier.code || ''}
                    imageAlt={marketingCarrier.name}
                    key={marketingCarrier.code}
                    logo={marketingCarrier.logo}
                  />
                  <Text as="p" className={optionsLengthStyles}>
                    {t(
                      'BrandedFares.NumberOfTrips.Text',
                      isOpen ? brandedFares?.length : alwaysVisibleOptions?.length,
                      brandedFares?.length,
                    )}
                  </Text>
                </Stack>
              </Inline>
              {brandedFareDescription && <p>{brandedFareDescription}</p>}
              <Inline>
                <TripDetailsTitle toggleTripDetailsDrawer={toggleTripDetailsDrawer} />
              </Inline>
              <Stack className={cardsContainer}>
                <div
                  className={listingWrapperStyles(isMediumScreenWidth, alwaysVisibleOptions.length)}
                >
                  {alwaysVisibleOptions.map((brandedFareData, index) => (
                    <BrandedFare
                      brandedFareData={brandedFareData}
                      key={brandedFareData.id}
                      recommendedFareKey={recommendedFareKey}
                      shouldShowInformationBox={
                        index === 0 && shouldShowInformationBoxInFirstOption
                      }
                      ticketingCarrierCodes={ticketingCarrierCodes}
                    />
                  ))}
                </div>
                {toggleableOptions && (
                  <ExpandableContent isExpanded={isOpen}>
                    <div className={listingWrapperStyles(isMediumScreenWidth)}>
                      {toggleableOptions.map((brandedFareData) => (
                        <BrandedFare
                          brandedFareData={brandedFareData}
                          key={brandedFareData.id}
                          ticketingCarrierCodes={ticketingCarrierCodes}
                        />
                      ))}
                    </div>
                  </ExpandableContent>
                )}
              </Stack>
              {isContentToggleable && (
                <div className={toggleButtonWrapperStyles}>
                  <Button
                    className={toggleButtonStyles}
                    data-testid="brandedFares-toggleOptions-button"
                    iconSuffix={<CaretDown className={iconStyles(isOpen)} />}
                    onClick={handleToggle}
                    size="small"
                  >
                    {isOpen
                      ? t('BrandedFares.NumberOfTrips.ShowLess.Label')
                      : t('BrandedFares.NumberOfTrips.ShowMore.Label', toggleableOptions.length)}
                  </Button>
                </div>
              )}
            </Stack>
          </RadioGroup>
        </Card>
        <Overlay.Drawer
          dataTestId="your-selected-trip-itinerary-drawer"
          handleClose={() => setShowTripDetails(false)}
          isOpen={showTripDetails}
        >
          <Overlay.Header
            onClose={() => setShowTripDetails(false)}
            title={t('IbeClient.TripInfo.Header')}
          />
          <Overlay.Content data-testid="itinerary-drawer-content">
            {modifiedBounds?.map((bound, index) => (
              <Card
                className={cardStyles}
                data-testid={`trip-${bound.boundId}-details`}
                key={bound.boundId}
                padding={0}
              >
                <ItineraryBound
                  boundId={bound.boundId}
                  boundIndex={index}
                  isSelected
                  journeyType={tripType}
                  segments={bound.segments}
                />
              </Card>
            ))}
          </Overlay.Content>
        </Overlay.Drawer>
      </ContentWrapper>
      <StickyFooter
        bounds={bounds}
        dataTestid="branded-fares-v2-sticky-footer"
        discountAmount={discountAmount}
        finalProductIdsToPurchase={finalProductIdsToPurchase}
        isSelectTripLoading={isSelectTripLoading}
        onClick={() => handleProceedButtonClick(finalProductIdsToPurchase)}
        paymentMethodPrices={paymentMethodPrices}
        selectedBrandedFare={selectedBrandedFare}
        totalPrice={totalPrice}
        travelerPrices={travelerPrices}
        travelerPricesWithoutDiscounts={selectedBrandedFare?.travelerPricesWithoutPaymentDiscounts}
        tripTravelers={tripTravelers}
      />
    </>
  );
};

BrandedFares.propTypes = {
  availableExtraProducts: PropTypes.arrayOf(ExtraProduct),
  bounds: PropTypes.arrayOf(BoundNew),
  brandedFares: PropTypes.arrayOf(BrandedFareType),
  defaultSelectedFareKey: PropTypes.string,
  discountAmount: PropTypes.any,
  handleProceedButtonClick: PropTypes.func,
  isMediumScreenWidth: PropTypes.bool,
  isSelectTripLoading: PropTypes.bool,
  marketingCarrier: CarrierNew,
  partner: PropTypes.string,
  paymentMethodPrices: PropTypes.any,
  recommendedFareKey: PropTypes.string,
  selectedBrandedFareSelectionKey: PropTypes.string,
  setSelectedBrandedFareSelectionKey: PropTypes.func,
  ticketingCarrierCodes: PropTypes.arrayOf(PropTypes.string),
  travelerPrices: PropTypes.arrayOf(TravelerPrice),
  tripTravelers: PropTypes.any,
  tripType: PropTypes.oneOf([MULTI_STOP, ONE_WAY, RETURN]),
};

export default BrandedFares;
